import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles, Cpu, AlertTriangle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Stress/risk level colors
const LEVEL_COLORS = {
  Low: '#00ff88',
  Medium: '#ffaa00',
  High: '#ff4466'
};

const PartIdentifier = ({ isOpen, onClose, viewerRef, modelInfo }) => {
  const [partInfo, setPartInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Auto-dismiss after 15 seconds
  useEffect(() => {
    if (isOpen && partInfo && !isLoading) {
      const timer = setTimeout(() => {
        onClose();
      }, 15000);
      return () => clearTimeout(timer);
    }
  }, [isOpen, partInfo, isLoading, onClose]);

  // Identify part when opened
  useEffect(() => {
    if (isOpen && !partInfo && !isLoading) {
      identifyPart();
    }
  }, [isOpen]);

  // Reset when closed
  useEffect(() => {
    if (!isOpen) {
      setPartInfo(null);
      setError(null);
    }
  }, [isOpen]);

  const captureScreenshot = () => {
    if (viewerRef?.current?.captureScreenshot) {
      try {
        return viewerRef.current.captureScreenshot();
      } catch (e) {
        console.error('Failed to capture screenshot:', e);
      }
    }
    return null;
  };

  const identifyPart = async () => {
    setIsLoading(true);
    setError(null);

    const imageData = captureScreenshot();
    if (!imageData) {
      setError('Unable to capture viewport');
      setIsLoading(false);
      return;
    }

    const prompt = `A user selected a part in this 3D CAD engine assembly. The selected part is highlighted/glowing. Identify the highlighted part. Respond ONLY in this exact JSON format: { "name": "part name", "function": "what it does in 1 sentence", "material": "likely material", "stress": "Low or Medium or High" }`;

    try {
      const response = await fetch(`${BACKEND_URL}/api/ai/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          image: imageData,
          question: prompt,
          context: modelInfo ? `Model: ${modelInfo.name}` : ''
        })
      });

      if (!response.ok) throw new Error('API request failed');

      const data = await response.json();
      
      // Try to parse JSON from response
      try {
        let jsonStr = data.response;
        // Extract JSON object from response
        const jsonMatch = jsonStr.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          jsonStr = jsonMatch[0];
        }
        const parsed = JSON.parse(jsonStr);
        if (parsed.name) {
          setPartInfo(parsed);
        } else {
          throw new Error('Invalid format');
        }
      } catch (parseError) {
        console.error('JSON parse error:', parseError);
        // Fallback
        setPartInfo({
          name: 'Unknown Part',
          function: data.response.substring(0, 150),
          material: 'Metal alloy',
          stress: 'Medium'
        });
      }
    } catch (err) {
      console.error('Part identification failed:', err);
      setError('Failed to identify part');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed top-20 left-1/2 -translate-x-1/2 z-50"
        initial={{ opacity: 0, y: -20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -20, scale: 0.95 }}
        transition={{ type: 'spring', stiffness: 400, damping: 30 }}
        data-testid="part-identifier"
      >
        <div
          className="rounded-xl overflow-hidden shadow-2xl min-w-[280px]"
          style={{
            background: 'rgba(10, 10, 25, 0.95)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.08)'
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-2 border-b border-white/10">
            <div className="flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5 text-cyan-400" />
              <span className="text-xs font-medium text-gray-400">✦ Part Identified</span>
            </div>
            <button
              onClick={onClose}
              className="w-6 h-6 rounded flex items-center justify-center text-gray-500 hover:text-white hover:bg-white/10 transition-colors"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Content */}
          <div className="px-4 py-3">
            {isLoading ? (
              <div className="flex items-center gap-2 py-2">
                <div className="w-4 h-4 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
                <span className="text-gray-400 text-sm">Identifying...</span>
              </div>
            ) : error ? (
              <p className="text-red-400 text-sm">{error}</p>
            ) : partInfo ? (
              <div className="space-y-2">
                {/* Part name */}
                <h3 className="text-base font-bold text-white">{partInfo.name}</h3>
                
                {/* Function */}
                <p className="text-sm text-gray-300 leading-relaxed">{partInfo.function}</p>
                
                {/* Material & Stress */}
                <div className="flex items-center gap-3 pt-1">
                  <div className="flex items-center gap-1.5">
                    <Cpu className="w-3.5 h-3.5 text-gray-500" />
                    <span className="text-xs text-gray-400">{partInfo.material}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <AlertTriangle 
                      className="w-3.5 h-3.5" 
                      style={{ color: LEVEL_COLORS[partInfo.stress] || LEVEL_COLORS.Medium }}
                    />
                    <span 
                      className="text-xs font-medium"
                      style={{ color: LEVEL_COLORS[partInfo.stress] || LEVEL_COLORS.Medium }}
                    >
                      {partInfo.stress} Stress
                    </span>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
          
          {/* Auto-dismiss indicator */}
          {partInfo && !isLoading && (
            <div className="h-0.5 bg-white/5">
              <motion.div
                className="h-full bg-cyan-500/50"
                initial={{ width: '100%' }}
                animate={{ width: '0%' }}
                transition={{ duration: 8, ease: 'linear' }}
              />
            </div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PartIdentifier;
